import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { GroupWork } from '@mui/icons-material';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { MaterialHeader } from '../layout/components/MaterialHeader.tsx';
import { MaterialTitle } from '../layout/components/MaterialTitle.tsx';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    ParkingaboCustomerPaymentMode,
    VehicleIdentification,
    OperatingModeText,
} from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';
import { useNavigate } from 'react-router';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

export interface RoleActive {
    roleId: string;
    name: Message;
    operatingMode: OperatingMode;
    customerCount: number;
    productTemplates: RoleProductTemplate[];
}

interface OperatingMode {
    operatingModeId: number;
    vehicleIdentification: VehicleIdentification[];
    paymentMode: ParkingaboCustomerPaymentMode;
}

interface RoleProductTemplate {
    productTemplateVersionId: string;
    name: Message;
}

export function OperatorParkingaboRoles() {
    const { language } = useLanguage();
    const [roleState] = useServerFetch<RoleActive[], object>(
        () => ({
            url: '/ui-api/operator-account/parkingabo/active-roles',
        }),
        {},
    );

    return (
        <MainContent>
            <MaterialHeader editMode={false}>
                <MaterialTitle
                    Icon={GroupWork}
                    title={
                        <Localized
                            de="Rollen"
                            fr="Rôles"
                            it="Ruoli"
                            en="Roles"
                        />
                    }
                />
            </MaterialHeader>
            <SeparationBar />
            <OperatorAsyncLoadedSection
                requestState={roleState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={roles => {
                    if (roles.length === 0) {
                        return <EmptyResults hasFilter={false} />;
                    }

                    const operatingModeIds = roles.map(
                        role => role.operatingMode.operatingModeId,
                    );
                    const showOperatingModeColumn =
                        new Set(operatingModeIds).size > 1;

                    return (
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Name"
                                                fr="Nom"
                                                it="Nome"
                                                en="Name"
                                            />
                                        </TableCell>
                                        {showOperatingModeColumn && (
                                            <TableCell>
                                                <Localized
                                                    de="Betriebsart"
                                                    fr="Modalité de gestion"
                                                    it="Modalità operativa"
                                                    en="Operating mode"
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Localized
                                                de="Produkttypen"
                                                fr="Types de produits"
                                                it="Tipi di prodotto"
                                                en="Product types"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Anzahl Benutzer"
                                                fr="Nombre d'utilisateurs"
                                                it="Numero utenti"
                                                en="Number of users"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {roles
                                        .sort((a, b) =>
                                            a.name[language].localeCompare(
                                                b.name[language],
                                            ),
                                        )
                                        .map(role => (
                                            <RoleRow
                                                key={role.roleId}
                                                role={role}
                                                showOperatingModeColumn={
                                                    showOperatingModeColumn
                                                }
                                            />
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }}
            />
        </MainContent>
    );
}

function RoleRow({
    role,
    showOperatingModeColumn,
}: {
    role: RoleActive;
    showOperatingModeColumn: boolean;
}) {
    const navigate = useNavigate();
    const {
        operatorData: { operatorExternalId },
    } = useOperatorContext();
    return (
        <TableRow>
            <TableCell>
                <Localized {...role.name} />
            </TableCell>
            {showOperatingModeColumn && (
                <TableCell>
                    <OperatingModeText
                        vehicleIdentification={
                            role.operatingMode.vehicleIdentification
                        }
                        paymentMode={role.operatingMode.paymentMode}
                    />
                </TableCell>
            )}
            <TableCell>
                <RoleProducts products={role.productTemplates} />
            </TableCell>
            <TableCell>
                <Box
                    sx={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        display: 'flex',
                        flexShrink: 1,
                    }}
                    onClick={() =>
                        navigate(
                            `/${operatorExternalId}/parkingabo/users?ROLE=${role.roleId}`,
                        )
                    }
                >
                    {role.customerCount}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                    }}
                />
            </TableCell>
        </TableRow>
    );
}

function RoleProducts({ products }: { products: RoleProductTemplate[] }) {
    if (products.length === 0) {
        return <>{EmDash}</>;
    }

    if (products.length <= 3) {
        return <ProductsList products={products} />;
    }

    return (
        <Tooltip
            title={<ProductsList products={products} />}
            placement="right-start"
        >
            <Box component="span" sx={{ cursor: 'help' }}>
                {products.length}
            </Box>
        </Tooltip>
    );
}

function ProductsList({ products }: { products: RoleProductTemplate[] }) {
    const { language } = useLanguage();

    return (
        <>
            {products
                .sort((a, b) =>
                    a.name[language].localeCompare(b.name[language]),
                )
                .map(product => (
                    <Box key={product.productTemplateVersionId}>
                        <Localized {...product.name} />
                    </Box>
                ))}
        </>
    );
}
